import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UrlUtils } from '../../shared/utils/url.utils';
import { ProfileOutputService } from './profile-output.service';
import {
    ActivatedRoute,
    Router,
    RouterLinkActive,
    RouterLink,
    RouterOutlet,
} from '@angular/router';
import { ProfileService } from '../profile.service';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ProfileDataService } from '../profile-data.service';
import { SEOService } from '../../core/seo.service';
import {
    ExportersService,
    GrowInAnimation,
    MuloNavLink,
    OpacityInAnimation,
    OpacityOutAnimation,
    TabsChildrenHorizontalAnimation,
    TabsChildrenVerticalAnimation,
    FunkyButtonComponent,
    BreadcrumbsNavComponent,
    VerticalTabsDirective,
} from '@exl-ng/mulo-common';
import { ResearcherOutputListFacetCategoryWithSubCategories } from '../../shared/interfaces/researcher-output-list-facet-category-with-sub-categories.interface';
import { AuthorizationService } from '../../core/authorization.service';
import { MediaService } from '@exl-ng/mulo-core';
import { map, Subject, Subscription, takeUntil } from 'rxjs';
import { I18nService } from '../../core/i18n.service';
import { MatTreeFlattener } from '@angular/material/tree';
import { AssetExportService } from '../../asset-page/asset-page-export/asset-export.service';
import { MatButton } from '@angular/material/button';
import { MatTabNav, MatTabLink, MatTabNavPanel } from '@angular/material/tabs';
import { AutomationClassDirective } from '../../shared/directives/tests/automation-tests-class.directive';
import { ContentLoaderModule } from '@ngneat/content-loader';
import { NavMainLayoutComponent } from '@exl-ng/mulo-views';
import { MatIcon } from '@angular/material/icon';
import { DepositFormTriggerDirective } from '../../deposit/deposit-form-trigger.directive';
import { EmptyStateComponent } from '../../parts/empty-state/empty-state.component';
import { NgClass } from '@angular/common';

/** Flat node with expandable and level information */
interface FlatNode extends ResearcherOutputListFacetCategoryWithSubCategories {
    expandable: boolean;
    level: number;
}

@Component({
    selector: 'esp-profile-output',
    templateUrl: './profile-output.component.html',
    styleUrls: ['./profile-output.component.scss'],
    host: { class: 'esp-profile-output' },
    animations: [
        TabsChildrenVerticalAnimation,
        TabsChildrenHorizontalAnimation,
        GrowInAnimation,
        OpacityInAnimation,
        OpacityOutAnimation,
    ],
    imports: [
        EmptyStateComponent,
        DepositFormTriggerDirective,
        FunkyButtonComponent,
        MatIcon,
        NavMainLayoutComponent,
        ContentLoaderModule,
        BreadcrumbsNavComponent,
        AutomationClassDirective,
        MatTabNav,
        VerticalTabsDirective,
        MatTabLink,
        RouterLinkActive,
        NgClass,
        RouterLink,
        MatButton,
        RouterOutlet,
        MatTabNavPanel,
        TranslateModule,
    ],
})
export class ProfileOutputComponent implements OnInit, OnDestroy {
    profileOutputService = inject(ProfileOutputService);
    private profileService = inject(ProfileService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private SEOService = inject(SEOService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private translate = inject(TranslateService);
    private profileDataService = inject(ProfileDataService);
    private authorizationService = inject(AuthorizationService);
    media = inject(MediaService);
    i18nService = inject(I18nService);
    private assetExportSvc = inject(AssetExportService);
    private exportersService = inject(ExportersService);
    private xl8Svc = inject(TranslateService);

    //@HostBinding('class.is-editable') @Input() editable = false;

    institutionCode: string;
    isLoading = true;

    nameSubscription: Subscription;
    refreshDataSubscription: Subscription;
    deleteSubscription: Subscription;

    tabTravelDistance = '50px';
    tabDirection: string;
    level = 0;
    allNodes = [];

    private _transformer = (node, level: number) => {
        const newNode: FlatNode = {
            ...node,
            expandable: !!node.subCategories && node.subCategories.length > 0,
            level: level,
        };
        if (!this.allNodes.includes(newNode)) {
            this.allNodes.push(newNode);
        }
        return newNode;
    };

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.subCategories,
    );

    ngOnInit() {
        this.institutionCode = UrlUtils.getParam('institution');
        this.getHighlightsFullList();
        this.getFacetsListFromServer();
        this.subscribeToRefreshDataAfterSaveAsset();
        this.subscribeToRefreshFullMMSListAfterDelete();
        this.getFullMMSListForExport();
        this.tabDirection = this.tabTravelDistance;

        this.route.data.subscribe((res) => {
            //  deep link to the direct tab - need to wait until the name updates
            this.nameSubscription = this.profileService.nameChange$.subscribe(
                (res) => {
                    this.changeDocumentTitle(res);
                },
            );
        });
    }

    trackItem = (idx, item) => item.category.facet;
    getHighlightsFullList() {
        this.profileOutputService.getHighlightsFullList().subscribe((data) => {
            this.profileOutputService.highlights = data as string[];
        });
    }

    disableExportAll() {
        if (
            this.isOutputEmpty ||
            (this.profileOutputService.facets.length === 1 &&
                this.profileOutputService.facets[0].category.facet === 'drafts')
        ) {
            return true;
        }
        return false;
    }

    setTabDir(old, next) {
        let string;
        if (old > next) {
            string = '-' + this.tabTravelDistance;
        } else {
            string = this.tabTravelDistance;
        }
        return string;
    }

    subscribeToRefreshFullMMSListAfterDelete() {
        this.deleteSubscription =
            this.profileOutputService.assetDeleted$.subscribe(() => {
                this.getFullMMSListForExport();
            });
    }

    getFullMMSListForExport() {
        this.profileOutputService
            .getFullMMSList(this.isPublicPage)
            .subscribe((data) => {
                this.profileOutputService.fullMMSList =
                    data as unknown as string[];
            });
    }

    subscribeToRefreshDataAfterSaveAsset() {
        this.refreshDataSubscription =
            this.profileOutputService.refreshData$.subscribe((data) => {
                this.allNodes = this.treeFlattener.flattenNodes(
                    this.profileOutputService.facets,
                );
                if (data) {
                    this.navigateToFirstResourceTypeInDisplay();
                } else {
                    this.calcSelectedIndex();
                }
                this.getFullMMSListForExport();
            });
    }

    calcSelectedIndex() {
        if (
            this.route.snapshot.firstChild &&
            this.route.snapshot.firstChild.params
        ) {
            const id = this.route.snapshot.firstChild.params.id;
            // const facets = this.profileOutputService.getFacetNames();
            const facets = this.getFilteredArray(id);
            if (facets.length > 0) {
                const index = facets.indexOf(id);
                if (index !== -1) {
                    this.selectedIndex = index;
                    this.selectedIndexForMobile =
                        this.getSelectedIndexForMobile(id);
                } else {
                    //when submitting a single draft for example, need to navigate to in process
                    this.navigateToFirstResourceTypeInDisplay();
                }
            }
        }
    }

    getSelectedIndexForMobile(facet) {
        return this.profileOutputService.getFacetNames() &&
            this.profileOutputService.getFacetNames().length > 0
            ? this.profileOutputService.getFacetNames().indexOf(facet)
            : -1;
    }

    changeDocumentTitle(userName, facet?: string) {
        let code = '';
        if (this.profileService.isPublicPage) {
            code = 'research.public.profile.output.tab.document.title';
        } else {
            code = 'research.profile.output.tab.document.title';
        }

        const facetName = this.getFacetName(facet);
        const facetForTranslate = this.getOutputTitle(facetName);
        const titleSuffix = this.translate.instant(facetForTranslate);

        this.translate
            .get(code, {
                value0: userName,
                value1: this.configurationHandlerService.mainInstitutionName(),
                value2: titleSuffix,
            })
            .subscribe((res) => {
                this.SEOService.updateTitle(res);
            });
    }

    private getFacetName(facetName?: string) {
        return (
            facetName ??
            this.route.snapshot.firstChild?.data?.resourceType ??
            this.profileOutputService?.flattenFacetsList[this.selectedIndex]
                ?.facet ??
            ''
        );
    }

    getFacetsListFromServer() {
        this.isLoading = true;
        this.profileOutputService
            .getOutputListFacets(this.isPublicPage)
            .subscribe((data) => {
                const facets =
                    data as ResearcherOutputListFacetCategoryWithSubCategories[];
                if (facets && facets.length > 0) {
                    this.profileOutputService.saveFacets(data);
                    this.navigateToElementInList();
                } else {
                    this.profileOutputService.isOutputEmpty = true;
                }
                this.isLoading = false;
            });
    }

    navigateToElementInList() {
        if (!this.route.snapshot.firstChild) {
            this.navigateToFirstResourceTypeInDisplay();
        } else {
            const id = this.route.snapshot.firstChild.params.id;
            const facets = this.getFilteredArray(id);
            const index = facets.indexOf(id);
            if (index !== -1) {
                this.selectedIndex = index;
                this.selectedIndexForMobile =
                    this.getSelectedIndexForMobile(id);
                this.router.navigate([id], {
                    queryParams: { institution: this.institutionCode },
                    relativeTo: this.route,
                    replaceUrl: true,
                }); //maybe delete!!!!
            } else {
                this.navigateToFirstResourceTypeInDisplay();
            }
        }

        this.changeDocumentTitle(this.profileDataService.displayName.value);
    }

    navigateToFirstResourceTypeInDisplay() {
        const outputGroup = this.profileOutputService.firstElementToDisplay();
        if (outputGroup) {
            this.router.navigate([outputGroup], {
                queryParams: { institution: this.institutionCode },
                relativeTo: this.route,
                replaceUrl: true,
            });
            this.profileOutputService.selectedIndex = 0;
            this.profileOutputService.selectedIndexForMobile = 0;
        }
    }

    onMobileFacetChange(selectedMobileFacet: MuloNavLink) {
        const selectedFacetName =
            selectedMobileFacet.name === 'all_assets'
                ? 'all'
                : selectedMobileFacet.name;
        const selectedFacet = this.findFacetByFacetCode(
            this.profileOutputService.facets,
            selectedFacetName,
        );
        this.updateSelectedIndex(selectedFacet);
        this.router.navigate([selectedFacet.category.facet], {
            queryParams: { institution: this.institutionCode },
            relativeTo: this.route,
            replaceUrl: true,
        });
    }

    findFacetByFacetCode(
        facets: ResearcherOutputListFacetCategoryWithSubCategories[],
        facetCode: string,
    ): ResearcherOutputListFacetCategoryWithSubCategories {
        let matchFacet = facets.find(
            (facet) => facet.category.facet === facetCode,
        );
        if (matchFacet) {
            return matchFacet;
        } else {
            for (let index = 0; index < facets.length; index++) {
                const currentFacet = facets[index];
                if (this.hasChildren(currentFacet)) {
                    matchFacet = this.findFacetByFacetCode(
                        currentFacet.subCategories,
                        facetCode,
                    );
                    if (matchFacet) {
                        return matchFacet;
                    }
                }
            }
        }
    }

    hasChildren = (tab: ResearcherOutputListFacetCategoryWithSubCategories) =>
        tab.subCategories.length > 0;

    showSubCategories(tab, active) {
        if (tab.category.facet === 'all' || active) {
            return true;
        }
        if (
            this.profileOutputService.chosenFacet &&
            this.profileOutputService.chosenFacet.indexOf('.') !== -1
        ) {
            const category = this.profileOutputService.chosenFacet.substring(
                0,
                this.profileOutputService.chosenFacet.indexOf('.'),
            );
            return tab.category.facet === category;
        }
        return false;
    }
    getBaseCategory = (category: string) =>
        category?.indexOf('.') > -1
            ? category.substring(0, category.indexOf('.'))
            : category;
    sameCatAsChosen(tab) {
        const chosenCat = this.getBaseCategory(
            this.profileOutputService.chosenFacet,
        );
        const thisCat = this.getBaseCategory(tab.category.facet);
        return chosenCat === thisCat;
    }

    updateSelectedIndex(facet) {
        const filtererdArray = this.getFilteredArray(facet.category.facet);
        const index = filtererdArray.indexOf(facet.category.facet);
        this.tabDirection = this.setTabDir(this.selectedIndex, index);
        this.selectedIndex = index;
        this.selectedIndexForMobile = this.getSelectedIndexForMobile(
            facet.category.facet,
        );

        this.changeDocumentTitle(
            this.profileDataService.displayName.value,
            facet.category.facet,
        );
    }

    getFilteredArray(facet) {
        const facetNames = this.profileOutputService.getFacetNames();
        const isCategory = facet.indexOf('.') === -1;
        let filtererdArray = [];
        if (isCategory) {
            filtererdArray = facetNames.filter(
                (facet) => facet.indexOf('.') === -1,
            );
        } else {
            const category = facet.substring(0, facet.indexOf('.'));
            filtererdArray = facetNames.filter(
                (facet) =>
                    facet.indexOf('.') === -1 ||
                    facet.startsWith(category + '.'),
            );
        }
        return filtererdArray;
    }

    get selectedIndex() {
        return this.profileOutputService.selectedIndex;
    }

    set selectedIndex(selectedIndex) {
        this.profileOutputService.selectedIndex = selectedIndex;
    }

    get selectedIndexForMobile() {
        return this.profileOutputService.selectedIndexForMobile;
    }

    set selectedIndexForMobile(selectedIndexForMobile) {
        this.profileOutputService.selectedIndexForMobile =
            selectedIndexForMobile;
    }

    getOutputTitle(link) {
        return this.profileOutputService.getOutputTitle(link);
    }

    get isOutputEmpty() {
        return this.profileOutputService.isOutputEmpty;
    }

    get isPublicPage() {
        return this.profileOutputService.isPublicPage();
    }

    exportAssets() {
        const dialogClosed = new Subject<null>();
        const xl8 = (key: string | string[], params?: Object) =>
            this.xl8Svc.instant(key, params);
        this.exportersService
            .showAssetExportDialog({
                dialogTitle: xl8(
                    'research.portal.fulldisplay.sendTo.export.title',
                ),
                dialogCancel: xl8(
                    'research.portal.fulldisplay.sendTo.export.close',
                ),
                labels: {
                    to: xl8('research.portal.fulldisplay.sendTo.export.to'),
                    copy: xl8('research.portal.fulldisplay.sendTo.export.copy'),
                    copied: xl8(
                        'research.portal.fulldisplay.sendTo.export.copy.success',
                    ),
                },
                selectedAssets: [],
                allAssets: this.profileOutputService.fullMMSList,
                selectedScope: 'all',
                exportTargets$: this.assetExportSvc.exportTargets$.pipe(
                    map((_) =>
                        _.filter(
                            (t) =>
                                !['email', 'print', 'endnote'].includes(
                                    t.value,
                                ),
                        ),
                    ),
                ),
            })
            .pipe(takeUntil(dialogClosed))
            .subscribe((change) => {
                if (change.event === 'actionClick') {
                    this.assetExportSvc.handleExportActionClick(
                        change,
                        this.profileOutputService.fullMMSList,
                    );
                }
                if (change.event === 'dialogClosed') {
                    this.assetExportSvc.clearOutput();
                    dialogClosed.next(null);
                    dialogClosed.complete();
                }
            });
    }

    isResearcherDepositsEnabled() {
        return this.authorizationService.isAuthorizedForDeposit();
    }

    displayApcButton(): boolean {
        const isApcEnabled =
            this.configurationHandlerService.isCustomerParameterEnabled(
                'esploro_oable_integration',
            );
        return isApcEnabled && !this.profileOutputService.isPublicPage();
    }

    openApcForm() {
        this.router.navigate(['/apc'], {
            queryParams: { institution: this.institutionCode },
        });
    }

    ngOnDestroy() {
        if (this.nameSubscription) {
            this.nameSubscription.unsubscribe();
        }
        if (this.refreshDataSubscription) {
            this.refreshDataSubscription.unsubscribe();
        }
        if (this.deleteSubscription) {
            this.deleteSubscription.unsubscribe();
        }
    }
}
